/**
 * CORE-1685 — палитра комплекта у поля ввода: одиннадцать картинок в ряд,
 * нажатие вставляет `:agape_N:` в черновик. Общая для композера чата и
 * комментариев; кнопку-переключатель держит хозяин, здесь только ряд.
 *
 * CORE-1479 — три картинки наградные (`utils/rewardReactions`). Пока награда
 * не открыта, но ещё может открыться, они стоят под замком, и нажатие
 * объясняет, до какого числа заполнить профиль; когда открыть её уже нельзя —
 * их в палитре нет. Правило здесь, а не у каждого хозяина: палитра одна на
 * семь мест, и забыть его в восьмом было бы легко.
 */
import React from 'react';
import { Pressable, ScrollView, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import { BRAND_REACTIONS } from '../../constants/brandReactions';
import { useRewardReactions } from '../../hooks/useRewardReactions';
import { useI18n } from '../../i18n';
import { ReactionGlyph } from './ReactionGlyph';
import { RewardLock } from './RewardLock';
import { useMessagesSurface, emojiStripLayout } from './messagesSurface';

interface Props {
  onPick: (type: string) => void;
  /** CORE-1686 — поднабор комплекта (чат Live-сессии); по умолчанию — весь. */
  types?: readonly string[];
  size?: number;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

export const EmojiStrip: React.FC<Props> = ({ onPick, types, size = 28, style, testID = 'emoji-strip' }) => {
  const { access, guardAdd } = useRewardReactions();
  const { t } = useI18n('common');
  const layout = emojiStripLayout(useMessagesSurface());
  const items = BRAND_REACTIONS
    .filter((r) => (!types || types.includes(r.type)) && access(r.type) !== 'hidden')
    .map((r) => {
      const locked = access(r.type) === 'locked';
      // CORE-1790 — подсказка и подпись доступности несут полный смысл
      // («Нет, против, несогласие, протест»), палитра остаётся картинками.
      const label = locked ? t('rewardReactions.lockedA11y', { label: r.hint }) : r.hint;
      const glyph = (
        <>
          <View style={locked ? styles.lockedGlyph : undefined}>
            <ReactionGlyph type={r.type} size={size} />
          </View>
          {locked && <RewardLock size={Math.max(10, Math.round(size * 0.4))} />}
        </>
      );
      return (
        <Pressable
          key={r.type}
          onPress={() => { if (guardAdd(r.type)) onPick(r.type); }}
          accessibilityRole="menuitem"
          accessibilityLabel={label}
          testID={locked ? `emoji-strip-${r.type}-locked` : `emoji-strip-${r.type}`}
          style={({ pressed }) => [styles.item, layout.cell != null && [styles.cell, { width: layout.cell, height: layout.cell }], pressed && styles.itemPressed]}
          // CORE-1730 — выбор картинки не забирает фокус у поля ввода: клавиатура телефона остаётся.
          {...(typeof document !== 'undefined'
            ? ({ title: label, onMouseDown: (e: { preventDefault(): void }) => e.preventDefault() } as object)
            : {})}
        >
          {/* CORE-1791 — в ячейке 44 замок держится угла картинки, а не ячейки:
              вид заблокированной награды остаётся прежним. */}
          {layout.cell != null ? <View style={styles.glyphBox}>{glyph}</View> : glyph}
        </Pressable>
      );
    });
  return (
    <View style={[styles.wrap, style]} testID={testID} accessibilityRole="menu">
      {layout.wrap ? (
        <View style={[styles.row, styles.rowWrap]}>{items}</View>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} keyboardShouldPersistTaps="always" contentContainerStyle={styles.row}>
          {items}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: { width: '100%' },
  row: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 8, paddingVertical: 6 },
  item: { padding: 4, borderRadius: 10 },
  itemPressed: { opacity: 0.6 },
  lockedGlyph: { opacity: 0.45 },
  // CORE-1791 — раздел сообщений: кнопка 44, картинка по центру с прежним полем 4.
  cell: { padding: 0, alignItems: 'center', justifyContent: 'center' },
  glyphBox: { position: 'relative', padding: 4 },
  rowWrap: { flexWrap: 'wrap', gap: 0 },
});
